import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, CircularProgress, Button,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
  getLotContentAttachments,
  deleteLotContentAttachment,
  uploadLotContentAttachment,
} from '../../../../services/lotUserContentService';
import { normalizeUserLotAttachmentFileName } from '../../../../services/userLotsService';
import { toast } from '../../../../Components/Toast';
import { confirmDialog } from '../../../../Components/ConfirmDialog';
import { typography } from '../../../../styles/theme';
import { FORM_SECONDARY_BUTTON_SX } from './formBlockUi';
import ListingAttachments, { ListingAttachmentsEmpty } from './ListingAttachments';
import {
  UF_MAX_ATTACHMENTS,
  UF_ATTACHMENT_ACCEPT,
  UF_ATTACHMENT_LIMIT_TOAST,
  UF_ATTACHMENT_INVALID_TOAST,
  validateUfAttachmentFile,
  normalizeListingAttachmentsFromPayload,
  resolveAttachmentApiMessage,
  isAttachmentLimitError,
  isAttachmentDuplicateError,
  isAttachmentTypeOrSizeError,
} from '../../../utils/listingAttachmentUtils';
import { resolveSpaceGuestWriteError } from '../../../utils/spaceMirrorLots';

const applyAttachments = (payload) => normalizeListingAttachmentsFromPayload(payload);

/**
 * Sección Adjuntos del contenido de lote (sin UF): subir imagen/PDF
 * (presign → PUT S3 → save), listar y borrar. Separada de LotContentImages
 * (fotos) y de UserLotAttachments (UF). Tope: 2.
 * `ownerUserId`: espacio ajeno, lee (F1) y sube (F2) los del owner (mismo
 * owner en presign y save). `additiveOnly`: sin borrar.
 */
const LotContentAttachments = ({
  lotId,
  canEdit,
  initialAttachments,
  onAttachmentsChange,
  ownerUserId = null,
  additiveOnly = false,
}) => {
  const canRemove = Boolean(canEdit) && !additiveOnly;
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const fileInputRef = useRef(null);

  const notifyChange = (next) => {
    const normalized = applyAttachments(next);
    setAttachments(normalized);
    onAttachmentsChange?.(normalized);
  };

  useEffect(() => {
    if (!lotId) return;
    if (Array.isArray(initialAttachments)) {
      setAttachments(applyAttachments(initialAttachments));
      setLoading(false);
      return;
    }
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotId, initialAttachments, ownerUserId]);

  const fetchAttachments = async () => {
    setLoading(true);
    try {
      const res = await getLotContentAttachments(lotId, { ownerUserId });
      if (res._httpOk) {
        notifyChange(res.data ?? res);
      }
    } catch (e) {
      console.error('Error fetching lot content attachments:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (files) => {
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0) return;

    const available = UF_MAX_ATTACHMENTS - attachments.length;
    if (available <= 0) {
      toast.warning(UF_ATTACHMENT_LIMIT_TOAST);
      return;
    }

    const toUpload = fileArray.slice(0, available);
    if (fileArray.length > available) {
      toast.warning({
        title: 'Límite de adjuntos',
        message: `Solo podés agregar ${available} adjunto${available !== 1 ? 's' : ''} más.`,
      });
    }

    setUploading(true);
    let errors = 0;
    let hitLimit = false;
    let successCount = 0;

    try {
      for (const file of toUpload) {
        const validation = validateUfAttachmentFile(file);
        if (!validation.ok) {
          errors += 1;
          toast.warning({
            title: UF_ATTACHMENT_INVALID_TOAST.title,
            message: validation.message || UF_ATTACHMENT_INVALID_TOAST.message,
          });
          continue;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await uploadLotContentAttachment(lotId, file, {
            validate: validateUfAttachmentFile,
            ownerUserId,
          });
          if (res?.data) {
            const fileName = normalizeUserLotAttachmentFileName(file?.name);
            const withName = fileName
              ? { ...res.data, file_name: fileName }
              : res.data;
            setAttachments((prev) => {
              const next = applyAttachments([...prev, withName]);
              onAttachmentsChange?.(next);
              return next;
            });
            successCount += 1;
          }
        } catch (e) {
          errors += 1;
          const api = e?.apiResponse;
          const msg = resolveAttachmentApiMessage(api) || e?.message || '';
          const status = api?._httpStatus;
          const spaceError = resolveSpaceGuestWriteError(status, ownerUserId);
          if (spaceError) {
            toast.error({ title: 'Error', message: spaceError });
            break;
          }
          if (isAttachmentLimitError(status, msg)) {
            hitLimit = true;
            break;
          }
          if (isAttachmentDuplicateError(status, msg)) {
            toast.warning({
              title: 'Adjunto duplicado',
              message: 'Ese archivo ya está guardado en este lote.',
            });
          } else if (isAttachmentTypeOrSizeError(status, msg)) {
            toast.warning({
              title: UF_ATTACHMENT_INVALID_TOAST.title,
              message: msg || UF_ATTACHMENT_INVALID_TOAST.message,
            });
          } else {
            toast.error({
              title: 'Error',
              message: msg || 'No se pudo subir el adjunto.',
            });
          }
        }
      }

      if (hitLimit) {
        toast.warning(UF_ATTACHMENT_LIMIT_TOAST);
      } else if (successCount > 0 && errors === 0) {
        toast.success(
          successCount > 1 ? 'Adjuntos subidos correctamente.' : 'Adjunto subido correctamente.',
        );
      } else if (successCount > 0 && errors > 0) {
        toast.warning({
          title: 'Parcialmente completado',
          message: `${successCount} subido(s). ${errors} no pudieron subirse.`,
        });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attachment) => {
    if (!canRemove || !attachment?.id) return;

    const result = await confirmDialog({
      title: '¿Eliminar adjunto?',
      description: 'Se eliminará de este lote. Esta acción no se puede deshacer.',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!result.isConfirmed) return;

    setDeletingIds((prev) => new Set([...prev, attachment.id]));
    setAttachments((prev) => {
      const next = prev.filter((item) => item.id !== attachment.id);
      onAttachmentsChange?.(next);
      return next;
    });

    try {
      const res = await deleteLotContentAttachment(attachment.id);
      if (!res._httpOk) {
        toast.error({
          title: 'Error',
          message: resolveAttachmentApiMessage(res) || 'No se pudo eliminar el adjunto.',
        });
        throw new Error('delete failed');
      }
      if (Array.isArray(res.data?.attachments)) {
        notifyChange(res.data.attachments);
      }
      toast.success('Adjunto eliminado con éxito');
    } catch (e) {
      if (e.message !== 'delete failed') {
        console.error('Error deleting lot content attachment:', e);
        toast.error({ title: 'Error', message: 'No se pudo eliminar el adjunto.' });
      }
      setAttachments((prev) => {
        const next = applyAttachments([...prev, attachment]);
        onAttachmentsChange?.(next);
        return next;
      });
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(attachment.id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  const atLimit = attachments.length >= UF_MAX_ATTACHMENTS;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      {attachments.length > 0 ? (
        <ListingAttachments
          attachments={attachments}
          canEdit={canEdit}
          deletingIds={deletingIds}
          onDelete={canRemove ? handleDelete : null}
        />
      ) : (
        canEdit ? <ListingAttachmentsEmpty /> : null
      )}

      {canEdit && !atLimit && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={UF_ATTACHMENT_ACCEPT}
            multiple
            hidden
            disabled={uploading}
            onChange={(e) => handleUpload(e.target.files)}
          />
          <Button
            variant="outlined"
            disabled={uploading}
            startIcon={
              uploading
                ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                : <AttachFileIcon sx={{ fontSize: 16 }} />
            }
            onClick={() => fileInputRef.current?.click()}
            sx={{
              ...FORM_SECONDARY_BUTTON_SX,
              alignSelf: 'stretch',
            }}
          >
            {uploading ? 'Subiendo…' : 'Agregar adjunto'}
          </Button>
        </>
      )}

      {canEdit && (
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.30)',
            fontSize: '11px',
            textAlign: 'center',
            fontFamily: typography.fontFamily.primary,
            fontStyle: atLimit ? 'italic' : 'normal',
          }}
        >
          {atLimit
            ? `Límite de ${UF_MAX_ATTACHMENTS} adjuntos alcanzado`
            : `Adjuntos ${attachments.length}/${UF_MAX_ATTACHMENTS} · JPEG/PNG ≤8 MB · PDF ≤10 MB`}
        </Typography>
      )}
    </Box>
  );
};

export default LotContentAttachments;
